/**
 * @file interpret/engine/browserDeps.ts
 * @brief Wires the real mic, socket, player and API into `InterpretController`.
 *
 * @author DES
 * @date 01-10-2026
 */

import { env } from "@/shared/config/env";
import { tokenStorage } from "@/shared/api/tokenStorage";
import { buildInterpretWsUrl, resolveTurnAudio } from "./endpoints";
import { openSystemAudio } from "@/features/employee-boardroom/capture/systemAudio";
import type { CallSend, InterpretDeps, MicStream } from "./interpretController";
import { openInterpretMic, startPcmCapture } from "./micCapture";
import { cancelS2sSession, endS2sSession, endS2sSessionOnUnload, startS2sSession } from "./s2sApi";
import { S2sSocket } from "./s2sSocket";
import { keepScreenAwake } from "./wakeLock";
import { WebPlayer } from "./webPlayer";

/** MediaStream behind the controller's small `MicStream` port. `stream` is
 *  what is interpreted; `micOnly` is your voice alone (Call mode sends it
 *  into the call). */
type BrowserMic = MicStream & { stream: MediaStream; micOnly: MediaStream };

/** Chromium's `AudioContext.setSinkId` (output device), not yet in the TS lib. */
type SinkableContext = AudioContext & { setSinkId?: (id: string) => Promise<void> };

async function openBrowserMic(
  deviceId?: string,
  opts?: { callAudio?: boolean },
): Promise<BrowserMic> {
  const mic = await openInterpretMic(deviceId, { callMode: !!opts?.callAudio });
  const micTrack = mic.getAudioTracks()[0];
  if (!opts?.callAudio) {
    return {
      stream: mic,
      micOnly: mic,
      stop: () => mic.getTracks().forEach((t) => t.stop()),
      onEnded: (cb) => micTrack?.addEventListener("ended", cb, { once: true }),
    };
  }

  // Call mode: you (mic) and them (the call's audio, without our own
  // translations) into one uplink. The server tells the two apart by
  // language, as it does for two people at one phone.
  let call: MediaStream;
  try {
    call = await openSystemAudio({ excludeOwnAudio: true });
  } catch (e) {
    mic.getTracks().forEach((t) => t.stop());
    throw Object.assign(e instanceof Error ? e : new Error(String(e)), { name: "CallAudioError" });
  }
  const ctx = new AudioContext();
  if (ctx.state === "suspended") await ctx.resume().catch(() => undefined);
  const out = ctx.createMediaStreamDestination();
  // Each source through its own gain (CallFloor shuts the mic when it would
  // hear the call or a translation on the speakers) and its own level meter.
  const micGain = ctx.createGain();
  const callGain = ctx.createGain();
  const micMeter = ctx.createAnalyser();
  const callMeter = ctx.createAnalyser();
  micMeter.fftSize = callMeter.fftSize = 1024;
  const micSrc = ctx.createMediaStreamSource(mic);
  const callSrc = ctx.createMediaStreamSource(call);
  micSrc.connect(micMeter);
  callSrc.connect(callMeter);
  micSrc.connect(micGain).connect(out);
  callSrc.connect(callGain).connect(out);
  const buf = new Float32Array(1024);
  const rms = (a: AnalyserNode) => {
    a.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i]! * buf[i]!;
    return Math.sqrt(sum / buf.length);
  };
  // Called every tick: schedule a fade only when the target changes.
  const targets = new Map<GainNode, number>();
  const fade = (g: GainNode, on: boolean) => {
    const target = on ? 1 : 0;
    if (targets.get(g) === target) return;
    targets.set(g, target);
    g.gain.setTargetAtTime(target, ctx.currentTime, 0.02);
  };
  const callTrack = call.getAudioTracks()[0];
  return {
    stream: out.stream,
    micOnly: mic,
    levels: () => ({ mic: rms(micMeter), call: rms(callMeter) }),
    setSources: ({ mic: micOn, call: callOn }) => {
      fade(micGain, micOn);
      fade(callGain, callOn);
    },
    stop: () => {
      mic.getTracks().forEach((t) => t.stop());
      call.getTracks().forEach((t) => t.stop());
      void ctx.close().catch(() => undefined);
    },
    onEnded: (cb) => {
      micTrack?.addEventListener("ended", cb, { once: true });
      callTrack?.addEventListener("ended", cb, { once: true });
    },
  };
}

/** Your live voice into the call's mic device (Call mode, option 2). Muted
 *  until the controller opens it; fades in and out over ~20 ms so the gate
 *  never clicks. */
async function openBrowserCallSend(mic: MicStream, deviceId: string): Promise<CallSend> {
  const ctx = new AudioContext() as SinkableContext;
  if (!ctx.setSinkId) {
    void ctx.close();
    throw new Error("This app cannot choose an audio output device.");
  }
  await ctx.setSinkId(deviceId);
  if (ctx.state === "suspended") await ctx.resume().catch(() => undefined);
  const gain = ctx.createGain();
  gain.gain.value = 0;
  ctx.createMediaStreamSource((mic as BrowserMic).micOnly).connect(gain);
  gain.connect(ctx.destination);
  return {
    setLive: (on) => gain.gain.setTargetAtTime(on ? 1 : 0, ctx.currentTime, 0.02),
    stop: () => {
      try {
        gain.disconnect();
      } catch {
        /* closed */
      }
      void ctx.close().catch(() => undefined);
    },
  };
}

export function createBrowserInterpretDeps(): InterpretDeps {
  return {
    api: {
      start: startS2sSession,
      end: endS2sSession,
      cancel: cancelS2sSession,
      endOnUnload: endS2sSessionOnUnload,
    },
    openMic: openBrowserMic,
    openCallSend: openBrowserCallSend,
    startCapture: (mic, onFrame) => startPcmCapture((mic as BrowserMic).stream, onFrame),
    createSocket: ({ fromLang, toLang, events }) =>
      new S2sSocket({
        url: () =>
          buildInterpretWsUrl(env.apiBaseUrl, tokenStorage.get() ?? "", window.location.origin),
        fromLang,
        toLang,
        events,
      }),
    player: new WebPlayer(),
    resolveAudio: (final, conversationId) =>
      resolveTurnAudio({ ...final, conversationId }, env.apiBaseUrl),
    authToken: () => tokenStorage.get(),
    keepAwake: keepScreenAwake,
    now: () => Date.now(),
  };
}
